import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Images } from "lucide-react";
import { uploadFile } from "@/lib/upload";

/**
 * Bulk photo replacement: tick the products, pick the same number of files, and
 * each file is matched to the products in the order they are listed.
 */
export const BulkImageTool = ({ products, refresh }) => {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState([]);
  const [progress, setProgress] = useState("");

  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const run = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (files.length !== picked.length) {
      toast.error(`You selected ${picked.length} product(s) but ${files.length} image(s)`);
      return;
    }
    let done = 0;
    for (const [i, id] of picked.entries()) {
      setProgress(`Uploading ${i + 1} of ${files.length}…`);
      try {
        const { url } = await uploadFile(files[i], "product");
        await api.patch(`/products/${id}/image`, { image: url });
        done += 1;
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail));
      }
    }
    setProgress("");
    setPicked([]);
    toast.success(`Replaced ${done} product photo(s)`);
    refresh();
  };

  return (
    <Card className="border-border mb-5" data-testid="bulk-image-tool">
      <CardContent className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-medium text-sm flex items-center gap-2"><Images size={15}/> Replace photos in bulk</div>
            <p className="text-xs text-muted-foreground mt-1">
              Tick the products below, then choose the same number of images — they are matched in the order shown.
            </p>
          </div>
          <Button variant="outline" className="btn-pill" onClick={() => setOpen((o) => !o)} data-testid="bulk-image-toggle">
            {open ? "Close" : "Start bulk replace"}
          </Button>
        </div>

        {open && (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-4">
              {products.map((p, i) => (
                <label
                  key={p.id}
                  className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs cursor-pointer transition-colors ${
                    picked.includes(p.id) ? "border-primary bg-primary/5" : "border-border"}`}
                  data-testid={`bulk-pick-${p.id}`}
                >
                  <input type="checkbox" checked={picked.includes(p.id)} onChange={() => toggle(p.id)} />
                  <span className="truncate">
                    {picked.includes(p.id) && <b className="text-primary mr-1">{picked.indexOf(p.id) + 1}.</b>}
                    {p.name}
                  </span>
                </label>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-3 mt-4">
              <input
                type="file"
                accept="image/*"
                multiple
                disabled={picked.length === 0 || Boolean(progress)}
                onChange={run}
                className="text-xs"
                data-testid="bulk-image-files"
              />
              <span className="text-xs text-muted-foreground" data-testid="bulk-image-status">
                {progress || `${picked.length} product(s) selected`}
              </span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default BulkImageTool;
